import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";

export interface WebhookIntegration {
    id: string;
    user_id: string;
    name: string;
    url: string;
    secret_raw: string | null;
    events: string[];
    enabled: boolean;
    created_at: string;
}

export interface WebhookDelivery {
    id: string;
    integration_id: string;
    event_type: string;
    payload: any;
    status: 'queued' | 'processing' | 'delivered' | 'failed' | 'retrying';
    attempts: number;
    max_attempts: number;
    next_retry_at: string | null;
    response_code: number | null;
    latency_ms: number | null;
    error_message: string | null;
    idempotency_key: string | null;
    created_at: string;
    delivered_at: string | null;
}

export function useWebhooks() {
    return useQuery({
        queryKey: ["webhooks"],
        queryFn: async () => {
            return api.getWebhooks();
        },
        retry: 1,
        staleTime: 30_000,
    });
}

export function useCreateWebhook() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (webhook: { name: string; url: string; secret_raw?: string | null; events: string[]; enabled: boolean }) => {
            return api.createWebhook(webhook);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["webhooks"] });
        },
    });
}

export function useUpdateWebhook() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, webhook }: { id: string; webhook: Partial<Omit<WebhookIntegration, 'id' | 'user_id' | 'created_at'>> }) => {
            return api.updateWebhook(id, webhook);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["webhooks"] });
        },
    });
}

export function useDeleteWebhook() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            return api.deleteWebhook(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["webhooks"] });
        },
    });
}

export function useWebhookDeliveries(webhookId: string) {
    return useQuery({
        queryKey: ["webhook_deliveries", webhookId],
        queryFn: async () => {
            if (!webhookId) return [];
            return api.getWebhookDeliveries(webhookId);
        },
        enabled: !!webhookId,
        refetchInterval: 10_000, // Poll deliveries every 10s while open
    });
}

export function useTestWebhook() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            return api.testWebhook(id);
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ["webhook_deliveries", id] });
        },
    });
}

export function useRetryWebhookDelivery() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, deliveryId }: { id: string; deliveryId: string }) => {
            return api.retryWebhookDelivery(id, deliveryId);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["webhook_deliveries", variables.id] });
        },
    });
}
